import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import emailjs from 'emailjs-com';
import './ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const { currentLanguage } = useSelector((state) => state.language);
  const formRef = useRef();
  
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    phone: '',
    subject: '',
    message: '',
    insurance_type: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

//   const EMAILJS_CONFIG = {
//     SERVICE_ID: 'service_lyvk1nq',
//     TEMPLATE_ID: 'template_zdp30ed',
//     USER_ID: 'oWWUKfD2JgWhVRiPq',
//     TO_EMAIL: 'info@sanad-ins.com'
//   };

const EMAILJS_CONFIG = {
    SERVICE_ID: 'service_48vgbnv', 
    TEMPLATE_ID: 'template_a7r5ni4',  
    USER_ID: 'c2MVQ4SGfFy62t155',  
    TO_EMAIL: 'salsabilohida@gmail.com'  
  };

  const insuranceTypes = [
    { value: '', label: { en: 'Select Insurance Type', ar: 'اختر نوع التأمين' } },
    { value: 'car', label: { en: 'Car Insurance', ar: 'تأمين المركبات' } },
    { value: 'travel', label: { en: 'Travel Insurance', ar: 'تأمين السفر' } },
    { value: 'property', label: { en: 'Property Insurance', ar: 'تأمين الممتلكات' } },
    { value: 'health', label: { en: 'Health Insurance', ar: 'التأمين الصحي' } },
    { value: 'business', label: { en: 'Business Insurance', ar: 'تأمين الأعمال' } },
    { value: 'other', label: { en: 'Other', ar: 'أخرى' } }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    if (submitStatus || errorMessage) {
      setSubmitStatus(null);
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSubmitStatus(null);

    try {
      const result = await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current,
        EMAILJS_CONFIG.USER_ID
      );

      console.log('Email sent successfully:', result.text);
      
      setSubmitStatus('success');
      
      setFormData({
        from_name: '',
        from_email: '',
        phone: '',
        subject: '',
        message: '',
        insurance_type: ''
      });

      // إغلاق المودال تلقائيًا بعد النجاح
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Failed to send email:', error);
      setErrorMessage(
        currentLanguage === 'en' 
          ? 'Failed to send message. Please try again.' 
          : 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.'
      );
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      from_name: '',
      from_email: '',
      phone: '',
      subject: '',
      message: '',
      insurance_type: ''
    });
    setSubmitStatus(null);
    setErrorMessage('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="contact-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div 
            className="contact-modal"
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close-btn" onClick={handleClose}>
              ✕
            </button>
            
            <div className="modal-header">
              <h2>{currentLanguage === 'en' ? 'Contact Us' : 'تواصل معنا'}</h2>
              <p>
                {currentLanguage === 'en' 
                  ? 'Send us a message and we\'ll get back to you shortly.' 
                  : 'أرسل لنا رسالة وسنرد عليك قريبًا.'}
              </p>
            </div>
            
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="success-message"
              >
                <span>✅</span>
                <div>
                  <p className="success-title">
                    {currentLanguage === 'en' 
                      ? 'Message Sent Successfully!' 
                      : 'تم إرسال الرسالة بنجاح!'}
                  </p>
                  <p className="success-detail">
                    {currentLanguage === 'en' 
                      ? 'We will contact you shortly. This window will close automatically.' 
                      : 'سنتواصل معك قريبًا. ستغلق هذه النافذة تلقائيًا.'}
                  </p>
                </div>
              </motion.div>
            )}
            
            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="error-message"
              >
                <span>❌</span>
                <div>
                  <p className="error-title">
                    {currentLanguage === 'en' 
                      ? 'Failed to Send Message' 
                      : 'فشل إرسال الرسالة'}
                  </p>
                  <p className="error-detail">{errorMessage}</p>
                </div>
              </motion.div>
            )}
            
            <form ref={formRef} onSubmit={handleSubmit} className="contact-modal-form">
              <input type="hidden" name="to_email" value={EMAILJS_CONFIG.TO_EMAIL} />
              
              <div className="modal-form-grid">
                <div className="form-group">
                  <label htmlFor="modal_name">
                    {currentLanguage === 'en' ? 'Full Name' : 'الاسم الكامل'} *
                  </label>
                  <input
                    type="text"
                    id="modal_name"
                    name="from_name"
                    value={formData.from_name}
                    onChange={handleChange}
                    required
                    placeholder={currentLanguage === 'en' ? 'Your name' : 'اسمك'}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="modal_email">
                    {currentLanguage === 'en' ? 'Email' : 'البريد الإلكتروني'} *
                  </label>
                  <input
                    type="email"
                    id="modal_email"
                    name="from_email"
                    value={formData.from_email}
                    onChange={handleChange}
                    required
                    placeholder={currentLanguage === 'en' ? 'your@email.com' : 'بريدك@الإلكتروني.com'}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="modal_phone">
                    {currentLanguage === 'en' ? 'Phone' : 'رقم الهاتف'}
                  </label>
                  <input
                    type="tel"
                    id="modal_phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={currentLanguage === 'en' ? 'Phone number' : 'رقم الهاتف'}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="modal_insurance">
                    {currentLanguage === 'en' ? 'Insurance Type' : 'نوع التأمين'}
                  </label>
                  <select
                    id="modal_insurance"
                    name="insurance_type"
                    value={formData.insurance_type}
                    onChange={handleChange}
                  >
                    {insuranceTypes.map((type, index) => (
                      <option key={index} value={type.value}>
                        {type.label[currentLanguage]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal_subject">
                  {currentLanguage === 'en' ? 'Subject' : 'الموضوع'} *
                </label>
                <input
                  type="text"
                  id="modal_subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder={currentLanguage === 'en' ? 'Subject of your message' : 'موضوع رسالتك'}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="modal_message">
                  {currentLanguage === 'en' ? 'Message' : 'الرسالة'} *
                </label>
                <textarea
                  id="modal_message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder={currentLanguage === 'en' ? 'Your message here...' : 'رسالتك هنا...'}
                ></textarea>
              </div>
              
              <div className="modal-form-actions">
                <motion.button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {currentLanguage === 'en' ? 'Sending...' : 'جاري الإرسال...'}
                    </>
                  ) : (
                    <>
                      <span className="send-icon">📨</span>
                      {currentLanguage === 'en' ? 'Send Message' : 'إرسال الرسالة'}
                    </>
                  )}
                </motion.button>
                
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={handleClose}
                >
                  {currentLanguage === 'en' ? 'Cancel' : 'إلغاء'}
                </button>
              </div>
              
              <div className="modal-privacy">
                <span className="lock-icon">🔒</span>
                <p>
                  {currentLanguage === 'en' 
                    ? 'Your information is secure and private.'
                    : 'معلوماتك آمنة وخاصة.'}
                </p>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;